/**
 * The rules of the `invites` section that are not a control — the bound its
 * threshold obeys, the verdict its rewards are judged by, and the ladder's one
 * ordering rule. Here rather than in the page for the reason `helpTypes.ts` and
 * `giftTypes.ts` are: the save bar and the row have to refuse the same things.
 */

import { isReward, parseReward, rewardProblemText } from "../../../lib/reward";
import { reasonFor, wholeInRange } from "./helpTypes";
import type { InviterRewardStep } from "./types";

/**
 * The ceiling on a threshold, and a loose one on purpose: the number is in the
 * game's own raw units, where a level is 20 and a lifetime score is millions.
 * It is here to catch a slipped keyboard, not to have an opinion about the
 * game's economy.
 */
export const TRIGGER_VALUE_MAX = 1000000000;

/**
 * What a reward box says when what is in it is not a reward.
 *
 * One line for both refusals: an operator looking at `{"coins": 500,` and one
 * looking at `600` are both being told what a reward IS. The save bar is where
 * the two part company — it names the field, so it can afford the longer
 * sentence (see `rewardProblemText`).
 */
export const NOT_A_REWARD =
  "Invalid JSON — a reward is an object or a list of items.";

/**
 * A reward that is not one yet, in the save bar's words.
 *
 * The BOX has its own line for this and names no field, because it is under the
 * field; the bar sits ten sections away and has to say which reward it is
 * refusing. Only a string can be wrong here: a finished reward is stored
 * parsed, and the normalizer drops everything else on the way in, so a string
 * in one of these keys is text somebody is still typing and nothing else.
 */
export function rewardFieldError(
  value: unknown,
  noun: string,
): string | undefined {
  if (typeof value !== "string") return undefined;
  const parsed = parseReward(value);
  return parsed.ok ? undefined : rewardProblemText(parsed.problem, noun);
}

/** The box's own line under a reward cell. Absent is not its business — the
 *  invitee may be paid nothing, and a step's missing reward says so itself. */
export function rewardCellError(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "string" || !isReward(value) ? NOT_A_REWARD : undefined;
}

/** One refusal, said twice: under the box, and in the save bar, which has to
 *  name the step because it is nowhere near it. */
export interface Complaint {
  row: string;
  bar: string;
}

/** What is wrong with one step, box by box. */
export interface StepErrors {
  from?: Complaint;
  to?: Complaint;
  name?: Complaint;
  reward?: Complaint;
}

const stepAt = (n: number) => `Inviter reward step ${n}`;

/**
 * A friend number: whole and at least one. No ceiling of the game's own — the
 * server reads any whole number — only the one past which a browser stops
 * counting exactly, since a number that is not the one typed would be worse.
 */
function friendComplaint(
  value: number | undefined,
  n: number,
  word: string,
): Complaint | undefined {
  if (value === undefined) {
    return { row: "Required", bar: `${stepAt(n)} has no ${word}.` };
  }
  const bad = wholeInRange(value, 1, Number.MAX_SAFE_INTEGER);
  return bad
    ? { row: bad, bar: `${stepAt(n)} has a ${word} ${reasonFor(bad)}.` }
    : undefined;
}

function rewardComplaint(
  reward: unknown,
  n: number,
): Complaint | undefined {
  if (reward === undefined) {
    return { row: "A step needs a reward", bar: `${stepAt(n)} needs a reward.` };
  }
  if (!rewardCellError(reward)) return undefined;
  const parsed = typeof reward === "string" ? parseReward(reward) : undefined;
  const why =
    parsed?.ok === false && parsed.problem === "invalid_json"
      ? "not valid JSON"
      : "not an object or a list";
  return { row: NOT_A_REWARD, bar: `${stepAt(n)} has a reward that is ${why}.` };
}

/**
 * Every step's problems, by the server's own reading of a ladder
 * (internal/invite/config.go parseLadder) — which refuses the WHOLE ladder over
 * any one of them, so each is worth a red box.
 *
 * The one ordering rule is the only one that looks at a neighbour: a step
 * starts after the step above it ends. It alone catches an overlap, a step out
 * of order and an open end that is not last. It is checked against a `to` that
 * is a number or the ∞, and not against a blank one — that box already says
 * what is wrong with it.
 */
export function ladderErrors(steps: InviterRewardStep[]): StepErrors[] {
  return steps.map(({ from, to, name, reward }, i) => {
    const n = i + 1;
    const errors: StepErrors = {};
    const above = i > 0 ? steps[i - 1].to : undefined;

    errors.from = friendComplaint(from, n, "first friend");
    if (!errors.from && from !== undefined) {
      if (above === null) {
        errors.from = {
          row: "The step above has no end",
          bar: `${stepAt(n)} follows a step with no end.`,
        };
      } else if (above !== undefined && from <= above) {
        errors.from = {
          row: `Must start after friend ${above}`,
          bar: `${stepAt(n)} must start after friend ${above}.`,
        };
      }
    }

    if (to !== null) {
      errors.to = friendComplaint(to, n, "last friend");
      if (!errors.to && to !== undefined && from !== undefined && to < from) {
        errors.to = {
          row: `Must end at friend ${from} or later`,
          bar: `${stepAt(n)} ends before it starts.`,
        };
      }
    }

    if (name === "") {
      errors.name = { row: "A step needs a name", bar: `${stepAt(n)} needs a name.` };
    }
    errors.reward = rewardComplaint(reward, n);
    return errors;
  });
}

/** Whether any step has anything wrong with it. */
export function stepHasErrors(errors: StepErrors): boolean {
  return !!(errors.from || errors.to || errors.name || errors.reward);
}

/** Why the ladder cannot be SAVED, if it cannot: the first step's first
 *  problem, box by box from the left, in the bar's words. */
export function inviterRewardsError(
  steps: InviterRewardStep[],
): string | undefined {
  for (const errors of ladderErrors(steps)) {
    const first = errors.from ?? errors.to ?? errors.name ?? errors.reward;
    if (first) return first.bar;
  }
  return undefined;
}

/** A run of friend numbers no step covers. */
export interface Hole {
  from: number;
  to: number;
}

/**
 * The numbers the inviter is paid nothing for: `before[i]` is the hole in front
 * of step i — friend 1 included, when the first step starts later — and
 * `tail` is the first friend past a last step that ends.
 *
 * For a ladder `ladderErrors` has no complaint about: over a broken one the
 * holes would describe a ladder the server does not run.
 */
export function ladderHoles(steps: InviterRewardStep[]): {
  before: (Hole | null)[];
  tail: number | null;
} {
  let next = 1;
  const before = steps.map((step) => {
    const from = step.from ?? next;
    const hole = from > next ? { from: next, to: from - 1 } : null;
    next = step.to === null || step.to === undefined ? Infinity : step.to + 1;
    return hole;
  });
  return {
    before,
    tail: steps.length > 0 && Number.isFinite(next) ? next : null,
  };
}

/** A hole as its row reads it: `2`, or `2–4`. */
export function holeLabel(hole: Hole): string {
  return hole.from === hole.to ? `${hole.from}` : `${hole.from}–${hole.to}`;
}
